import { useSearchQueryParam } from '@piaas/piaas-ui-toolkit/hooks';
import React, { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

import Auth from '../../../api/auth';
import useOrgPermissions from '../../../components/AppLayout/hooks/useOrgPermissions';
import DeleteFormModal from '../../../components/DeleteFormModal/DeleteFormModal';
import GitlabWikiHelpButton from '../../../components/GitlabWikiHelpButton';
import Table from '../../../components/Table';
import useApiQuery from '../../../hooks/useApiQuery';
import rbacCheck from '../../../rbac/rbacCheck';
import OrganizationBreadCrumb from '../OrganizationBreadCrumb';

import CreateProjectFormModal from './CreateProjectFormModal';
import EditProjectFormModal from './Project/EditProjectFormModal';

import useTableApiCall from '@/hooks/useTableApiCall';
import { useUserData } from '@/redux/selectors';
import type { APIHookArg } from '@/types';
import type { Project } from '@/types/projectTypes';
import type { TableRowData } from '@/types/tableTypes';

const usersGuideSlug = 'user-guide/organizations/projects';

const Projects = (): React.JSX.Element => {
  const { organizationId } = useParams();
  const intl = useIntl();
  const navigate = useNavigate();
  const userData = useUserData();
  const { isAdminOrg } = useOrgPermissions();

  const [createModalOpened, setCreateModalOpened] = useState<boolean>(false);

  const [projectDetails, setProjectDetails] = useState<Project>({} as Project);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [editModalOpened, setEditModalOpened] = useState<boolean>(false);
  const [rows, setRows] = useState<TableRowData[]>([]);

  const canCreateProject = rbacCheck(userData.roles, '/projects:POST') && !isAdminOrg;
  const canDeleteProject = rbacCheck(userData.roles, '/projects:DELETE');
  const canEditProject = rbacCheck(userData.roles, '/projects:PATCH');

  const handleEditProject = useCallback((project: Project) => {
    setEditModalOpened(true);
    setProjectDetails(project);
  }, []);

  const handleDeleteProject = useCallback((project: Project) => {
    setDeleteModalOpened(true);
    setProjectDetails(project);
  }, []);

  const transformProjectsToRows = useCallback(
    (data: Project[]) => {
      return data.map((project: Project) => {
        const isDeleteDisabled = !canDeleteProject || !!project.hasProducts;
        return {
          key: project._id,
          onRowClick: () => navigate(`/projects/${project._id ?? ''}`),
          data: [
            {
              field: 'name',
              value: project.name,
            },
            {
              field: 'description',
              value: project.description,
            },
            {
              field: 'technicalLead',
              value: project.technicalLead?.email,
            },
            {
              field: 'action',
              value: project,
              actions: [
                {
                  label: 'Edit',
                  onClick: handleEditProject,
                  disabled: !canEditProject,
                  tooltip: !canEditProject ? 'editProjectDisabled' : '',
                },
                {
                  label: 'Delete',
                  onClick: handleDeleteProject,
                  disabled: isDeleteDisabled,
                  tooltip: isDeleteDisabled ? 'projectDeleteDisabled' : '',
                },
              ],
              type: 'action',
            },
          ],
        };
      });
    },
    [canDeleteProject, canEditProject, handleDeleteProject, handleEditProject, navigate]
  );

  const handleRowsUpdate = useCallback(
    (data: unknown[]) => {
      setRows(transformProjectsToRows(data as Project[]) as TableRowData[]);
    },
    [transformProjectsToRows]
  );

  const [initialSearch] = useSearchQueryParam('search');

  const { fetchData, setCurrentPage, itemsPerPage, setItemsPerPage, ...tableData } =
    useTableApiCall(Auth.fetchV2Projects as unknown as (...args: unknown[]) => Promise<unknown>, {
      setRows: handleRowsUpdate,
      isPaginated: true,
      searchColumns: ['name', 'description'],
      endpointParams: {
        organizationId,
      },
      initialFilter: initialSearch,
      searchParamKey: 'search',
    });

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [setCurrentPage]
  );

  const { makeApiCall: deleteProjectCall } = useApiQuery(
    Auth.deleteV2Project as unknown as APIHookArg,
    {
      successMessage: 'projectDeleted',
    }
  );

  let createActionTooltip = '';
  if (!canCreateProject) {
    createActionTooltip = isAdminOrg
      ? intl.formatMessage({ id: 'adminOrgActionDisabled' })
      : intl.formatMessage({ id: 'projectCreationPermissionErr' });
  }

  const deleteProject = async (): Promise<void> => {
    setDeleteModalOpened(false);
    const data = await deleteProjectCall(projectDetails._id);

    if (data) {
      navigate(`/governance/organizations/${projectDetails.organizationId}/projects`);
    }

    fetchData();
  };

  const fields = [
    {
      title: { id: 'name' },
      id: 'name',
    },
    {
      title: { id: 'description' },
      id: 'description',
    },
    {
      title: { id: 'technicalLead' },
      id: 'technicalLead',
    },
    {
      title: { id: 'action' },
      id: 'action',
      type: 'action',
    },
  ];

  const tableError = tableData.error ? tableData.error.message : null;

  return (
    <>
      <OrganizationBreadCrumb />
      {createModalOpened && (
        <CreateProjectFormModal
          onSubmit={newProject => {
            setRows(prev => [
              transformProjectsToRows([newProject as Project])[0] as TableRowData,
              ...prev,
            ]);
          }}
          onCancel={() => {
            setCreateModalOpened(false);
          }}
        />
      )}
      {editModalOpened && (
        <EditProjectFormModal
          project={projectDetails}
          onSubmit={() => {
            fetchData();
          }}
          onCancel={() => {
            setEditModalOpened(false);
          }}
        />
      )}
      {deleteModalOpened && (
        <DeleteFormModal
          fieldName='Project'
          fieldValue={projectDetails.name}
          onSubmit={() => {
            deleteProject();
          }}
          onCancel={() => {
            setDeleteModalOpened(false);
          }}
        />
      )}
      <Table
        header={intl.formatMessage({ id: 'projects' })}
        fields={fields}
        rows={rows}
        pagination
        {...tableData}
        pageSize={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        error={tableError}
        setFilter={tableData.setFilter as (filter: unknown) => void}
        headerActionSlot={{
          content: (
            <GitlabWikiHelpButton
              slug={usersGuideSlug}
              tooltipMessageId='usersGuide'
              tooltipDefaultMessage='Users guide'
              modalTitleMessageId='usersGuide'
              modalTitleDefaultMessage='Users guide'
              buttonAriaLabel='Open users guide'
              buttonSx={{ mt: 0 }}
            />
          ),
        }}
        onPageChange={handlePageChange}
        createAction={{
          disabled: !canCreateProject,
          onClick: () => {
            setCreateModalOpened(true);
          },
          tooltip: createActionTooltip,
        }}
        advancedSearch={false}
      />
    </>
  );
};

export default Projects;
